// src/features/tasks/hooks/useTaskManuals.js
import { useState, useCallback } from 'react';
import { taskManualsAPI } from '../services/taskManuals';
import { useApi } from '../../../hooks/api/useApi';

export function useTaskManuals() {
  const [manuals, setManuals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { execute } = useApi();

  /**
   * List manuals linked to a task
   * @param {string} taskId - The task's ID
   * @returns {Promise<Object>} List of manual relationships
   */
  const listTaskManuals = useCallback(async (taskId) => {
    setLoading(true);
    setError(null);

    try {
      const result = await execute(() => taskManualsAPI.listTaskManuals(taskId));

      if (result?.success) {
        setManuals(result.data.data.manuals || []);
      }

      setLoading(false);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch task manuals';
      setError(errorMessage);
      setLoading(false);
      return {
        success: false,
        error: errorMessage,
      };
    }
  }, [execute]);

  /**
   * Link manual to a task
   * @param {string} taskId - The task's ID
   * @param {Object} data - Manual link data
   * @param {string} data.file_id - UUID of file to link as manual
   * @returns {Promise<Object>} Link result
   */
  const linkManualToTask = useCallback(async (taskId, data) => {
    setLoading(true);
    setError(null);

    try {
      const result = await execute(() =>
        taskManualsAPI.linkManualToTask(taskId, data)
      );

      if (result?.success) {
        // Refresh manuals list
        await listTaskManuals(taskId);
      }

      setLoading(false);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to link manual to task';
      setError(errorMessage);
      setLoading(false);
      return {
        success: false,
        error: errorMessage,
      };
    }
  }, [execute, listTaskManuals]);

  /**
   * Unlink manual from a task
   * @param {string} taskId - The task's ID
   * @param {string} taskManualId - The manual link's ID to remove
   * @returns {Promise<Object>} Unlink result
   */
  const unlinkManualFromTask = useCallback(async (taskId, taskManualId) => {
    setLoading(true);
    setError(null);

    try {
      const result = await execute(() =>
        taskManualsAPI.unlinkManualFromTask(taskId, taskManualId)
      );

      if (result?.success) {
        // Refresh manuals list
        await listTaskManuals(taskId);
      }

      setLoading(false);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to unlink manual from task';
      setError(errorMessage);
      setLoading(false);
      return {
        success: false,
        error: errorMessage,
      };
    }
  }, [execute, listTaskManuals]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Clear manuals state
   */
  const clearManuals = useCallback(() => {
    setManuals([]);
  }, []);

  return {
    loading,
    error,
    manuals,
    // Actions
    listTaskManuals,
    linkManualToTask,
    unlinkManualFromTask,
    clearError,
    clearManuals,
  };
}
